import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Project } from '../../../project/models/project';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, switchMap, map, take } from 'rxjs/operators';
import { ConfigurationDataService } from '../../services/configuration-data.service';
import { ProjectCreateComponent } from '../../components/project-create/project-create.component';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ProjectConfigurationBehaviorSubjectService } from "../../..//project/services/project-configuration-behavior-subject.service";

@Component({
  selector: 'app-project-configuration',
  template: require('./project-configuration.component.html'),
  styles: [require('./project-configuration.component.sass')]
})
export class ProjectConfigurationComponent implements OnInit {

  @ViewChild(ProjectCreateComponent) projectCreate: ProjectCreateComponent;

  projects$: Observable<Project[]>;
  projectId: number;
  configurations$: Observable<any[] | null>;
  activeConfig: any;
  latestConfig: any;
  schema: any;

  
  private readonly refreshProjectList$ = new BehaviorSubject(undefined);
  private readonly refreshProjectConfigurations$ = new BehaviorSubject(undefined);

  constructor(
    private projectDataService: ProjectDataService,
    private configurationDataService: ConfigurationDataService,
    private translate: TranslateService,
    private messageService: MessageService,
    private projectConfigurationBehaviorSubjectService: ProjectConfigurationBehaviorSubjectService,
    @Inject('ConfigurationStoreService') private configurationStoreService: any,
  ) {
    this.projects$ = this.refreshProjectList$.pipe(
      switchMap(() => this.projectDataService.getAllProjects()),
      map((projects) => projects.filter(project => project.id >= 0)),
      tap((projects) => {
        this.projectId = this.projectId || projects && projects[0] && projects[0].id;
        this.refreshProjectConfigurations$.next(undefined);
      })
    );
    this.configurations$ = this.refreshProjectConfigurations$.pipe(
      switchMap(() => {
        return this.projectId >= 0 ? this.configurationDataService.getProjectConfigurations(this.projectId) : of(null)
      }),
      tap((configurations) => {
        this.activeConfig = configurations && configurations[0];
        this.latestConfig = undefined;
        if (this.activeConfig) {
          this.configurationStoreService.setProjectConfiguration(this.projectId, this.activeConfig);
        }
        this.projectConfigurationBehaviorSubjectService.inform()
      })
    )
   }

  ngOnInit(): void {
    
  }

  onDataChanged = (data: any) => {
    this.latestConfig = data ? {
      ...data
    } : undefined;
  }

  async createNewConfiguration(): Promise<void>{
    const newConfig: any = {...this.latestConfig || this.activeConfig};
    delete newConfig._id;
    newConfig.activatedOn = new Date().getTime();
    newConfig.createdOn = new Date().getTime();
    this.activeConfig = undefined;
    try {
      await this.configurationDataService.createProjectConfiguration(this.projectId, newConfig).toPromise();
      this.refreshProjectConfigurations$.next(undefined);
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_CONFIGURATION"), 
        detail: `${this.translate.instant("I18N_CONFIGURATION.NEW_CONFIGURATION")} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
    }     
    catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_CONFIGURATION"), 
        detail: err.message
      });
    }
  }

  createNewProject = async() => {
    await this.projectCreate.showModal();
  }

  onProjectChange() : void {
    this.refreshProjectConfigurations$.next(undefined);
  }

  async activateConfig(configuration: any) : Promise<void> {
    try {
      await this.configurationDataService.updateProjectConfiguration(this.projectId, configuration).toPromise();
      this.refreshProjectConfigurations$.next(undefined);
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.ACTIVATE_CONFIGURATION"), 
        detail: `${this.translate.instant("I18N_CONFIGURATION.VERSION")} ${configuration.version} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_ACTIVATED")} `
      });
    }
    catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.ACTIVATE_CONFIGURATION"), 
        detail: err.message
      });
    }

  }

  viewConfig(configuration: any)  {
    this.activeConfig = {
      ...configuration
    }
  }

  onCreateProject = async (newProject: Project) => {
    try {
      await this.configurationDataService.createProjectConfiguration(newProject.id, {
        projectId: newProject.id,
        name: newProject.name,
        region: newProject.region,
        abbreviation: newProject.abbreviation
      }).toPromise();
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_PROJECT"), 
        detail: `${this.translate.instant("I18N_GENERAL.PROJECT")} "${newProject.name}" ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.projectId = newProject.id;
      this.refreshProjectList$.next(undefined);
    }
    catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_PROJECT"), 
        detail: err.message
      });
    }
  }

}
